import {useSpring, animated} from "react-spring";
import type {ReactNode} from "react";
import {forwardRef} from "react";
import {usePendingClick} from "../DSButton/button-helpers";
import {buttonStyles} from "../DSButton/DSButton.css";
import cx from "../../utils/cx";
import {elementThemes, hoverElementThemes} from "../../css/themes/themes.css";
import type {ElementTheme} from "../../css/themes/themes.css";
import {dsListTileStyles as styles} from "../DSListTile/DSListTile.css";

export type DSTagProps = {
  onMetaClick?: JSX.IntrinsicElements["button"]["onClick"];
  children: ReactNode;
  theme?: keyof typeof elementThemes | null;
  size?: keyof typeof styles.dsTagSize;
  hoverIcon?: ReactNode;
  hoverTheme?: ElementTheme;
} & Pick<
  JSX.IntrinsicElements["button"],
  "onClick" | "aria-label" | "className" | "style" | "autoFocus" | "disabled" | "id"
>;
export const DSTag = forwardRef<HTMLButtonElement, DSTagProps>((props, ref) => {
  const {
    children,
    onClick,
    onMetaClick,
    className,
    theme = null,
    size = "md",
    hoverIcon,
    hoverTheme,
    disabled,
    ...rest
  } = props;
  const {handleClick, icons, state} = usePendingClick({
    onClick,
    onMetaClick,
  });

  const contentStyles = useSpring({
    scale: state === "initial" ? 1 : 0.95,
    opacity: state === "initial" ? 1 : 0,
    config: {tension: 600, friction: 44},
  });

  return (
    <button
      className={cx(
        styles.base[theme ? "withTheme" : "default"],
        styles.asTag,
        styles.dsTagSize[size],
        onClick && styles.interactive[theme ? "withTheme" : "default"],
        theme && elementThemes[theme],
        hoverTheme && state === "initial" && hoverElementThemes[hoverTheme],
        className
      )}
      disabled={disabled}
      onClick={handleClick}
      ref={ref}
      type="button"
      {...rest}
    >
      {icons}
      <animated.div style={contentStyles}>{children}</animated.div>
      {hoverIcon && !disabled && state === "initial" && (
        <div className={buttonStyles.hoverIconOverlay}>{hoverIcon}</div>
      )}
    </button>
  );
});

export type NonInteractiveDSTagProps = Pick<
  DSTagProps,
  "size" | "theme" | "className" | "style" | "children"
>;

export const NonInteractiveDSTag = forwardRef<HTMLDivElement, NonInteractiveDSTagProps>(
  (props, ref) => {
    const {className, theme = null, size = "md", ...rest} = props;

    return (
      <div
        className={cx(
          styles.base[theme ? "withTheme" : "default"],
          styles.asTag,
          styles.dsTagSize[size],
          styles.dsTagSizeOutsideButton[size],
          theme && elementThemes[theme],
          className
        )}
        ref={ref}
        {...rest}
      />
    );
  }
);
